import React, { FC, Fragment, useCallback, useMemo } from "react";
import { Flex, useTheme } from "@chakra-ui/react";
import { Scroller } from "@ldp-web/component-library/ui/molecules/scroller";
import { HeaderGroupComponent } from "../header-group/header-group.component";
import { InfoCard } from "@ldp-web/component-library/ui/molecules/info-card";
import { Box } from "@ldp-web/component-library/ui/atoms/box";
import {
  IHeaderGroupComponentProps,
  TSDMContainerType,
  TSDMInfoCard,
} from "@ldp/common-types";
import { useSDMRouterContext } from "@ldp/sdm-contexts";
import { useRedirectLink } from "../../utils";

/** info card component prop types */
export interface IInfoCardContainerComponentProps {
  /** component Id */
  componentId?: string;
  /** content Id */
  contentId?: string;
  /** info cards list */
  infoCards: TSDMInfoCard[];
  /** header group */
  headerGroup?: IHeaderGroupComponentProps;
  /** container type */
  containerType?: TSDMContainerType;
  /** customized event handler on info card link click */
  infoCardClickHandler?: (code: string) => void;
}

/** renders infor card component */
export const InfoCardContainerComponent: FC<
  IInfoCardContainerComponentProps
> = ({
  infoCards,
  headerGroup,
  containerType = "Stack",
  infoCardClickHandler,
}) => {
  const {
    cardCollectionSpacing,
    element: { spacingMedium },
  } = useTheme();
  const router = useSDMRouterContext();
  const redirectToPage = useRedirectLink(router);
  const cardClickAction = useCallback(
    (url: string, code?: string) => {
      if (infoCardClickHandler && code) {
        infoCardClickHandler(code);
      } else {
        redirectToPage(url);
      }
    },
    [infoCardClickHandler]
  );

  const isScroller = containerType === "Scroll";
  const Wrapper = isScroller ? Scroller : Fragment;

  /** change the object structure to match info card */
  const updatedCardsList = useMemo(() => {
    if (!infoCards) return [];
    return infoCards.map((infoCard) => {
      const { isDeal, isLoyalty } = infoCard;
      return {
        ...infoCard,
        isDeal: isDeal ?? isLoyalty ?? false,
      } as TSDMInfoCard;
    });
  }, [infoCards]);

  if (!infoCards?.length) return null;

  return (
    <Box data-testid="info-cards-container" my={cardCollectionSpacing} w="full">
      {headerGroup && <HeaderGroupComponent {...headerGroup} noWrapper />}
      <Wrapper>
        <Flex
          rowGap={spacingMedium}
          columnGap={spacingMedium}
          as="ul"
          direction={isScroller ? "row" : "column"}
        >
          {updatedCardsList.map((infoCard) => {
            const { code, headline } = infoCard;
            return (
              <InfoCard
                key={`info-card-${code ?? headline}`}
                {...infoCard}
                cardClickAction={(url) => cardClickAction(url, code)}
                hideCtaChevron
              />
            );
          })}
        </Flex>
      </Wrapper>
    </Box>
  );
};

export default InfoCardContainerComponent;
